// src/Dashboard/pages/teacher/PrepaPhysique/hooks/usePrepaPhysiqueDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { getSettings } from "../../../../../services/settingsServices";
import { getGroupsList } from "../../../../../services/groupServices";

import {
  getAllAdherentsListWithGroup,
  getUserIdByAdherentId,
} from "../../../../../services/adherentServices";

import {
  getAllExercises,
  createExercise,
  editExercise,
  deleteExercise,
  getAllAdherentExerciseResults,
  addAdherentExerciseResult,
  updateAdherentExerciseResult,
  deleteAdherentExerciseResult,
} from "../../../../../services/prepaPhysiqueServices";

import { sendNotification } from "../../../../../services/notificationServices";

import {
  calculateAssignmentStats,
  filterAssignments,
  filterExercises,
  getDistinctProgrammesFromAssignments,
  getDistinctProgrammesFromExercises,
  seasonNeighbors,
} from "../utils/prepaPhysiqueUtils";

const DEFAULT_EXERCISE_FILTERS = {
  search: "",
  programme: "All",
};

const DEFAULT_ASSIGNMENT_FILTERS = {
  search: "",
  programme: "All",
  status: "All",
};

export function usePrepaPhysiqueDashboard() {
  const [activeTab, setActiveTab] = useState("exercices");

  const [exercises, setExercises] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [adherents, setAdherents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [seasons, setSeasons] = useState([]);

  const [exerciseFilters, setExerciseFilters] = useState(
    DEFAULT_EXERCISE_FILTERS
  );
  const [assignmentFilters, setAssignmentFilters] = useState(
    DEFAULT_ASSIGNMENT_FILTERS
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [exerciseDrawerOpen, setExerciseDrawerOpen] = useState(false);
  const [exerciseDrawerMode, setExerciseDrawerMode] = useState("create");
  const [currentExercise, setCurrentExercise] = useState(null);

  const [assignmentCreateDrawerOpen, setAssignmentCreateDrawerOpen] =
    useState(false);
  const [assignmentPresetExercise, setAssignmentPresetExercise] =
    useState(null);

  const [assignmentEditDrawerOpen, setAssignmentEditDrawerOpen] =
    useState(false);
  const [currentAssignment, setCurrentAssignment] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const [
        settingsData,
        exercisesData,
        assignmentsData,
        adherentsData,
        groupsData,
      ] = await Promise.all([
        getSettings(),
        getAllExercises(),
        getAllAdherentExerciseResults(),
        getAllAdherentsListWithGroup(),
        getGroupsList(),
      ]);

      const settings = Array.isArray(settingsData)
        ? settingsData[0]
        : settingsData;

      const activeSeason = settings?.saison_active || "";
      const seasonList = activeSeason
        ? [
            seasonNeighbors(activeSeason).prev,
            activeSeason,
            seasonNeighbors(activeSeason).next,
          ].filter(Boolean)
        : [];

      setSeasons(seasonList);
      setExercises(Array.isArray(exercisesData) ? exercisesData : []);
      setAssignments(Array.isArray(assignmentsData) ? assignmentsData : []);
      setAdherents(Array.isArray(adherentsData) ? adherentsData : []);
      setGroups(Array.isArray(groupsData) ? groupsData : []);
    } catch (error) {
      console.error("Erreur chargement préparation physique :", error);
      toast.error("Erreur lors du chargement de la préparation physique.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const refresh = useCallback(async () => {
    await loadData();
  }, [loadData]);

  const refreshExercises = useCallback(async () => {
    try {
      const data = await getAllExercises();
      setExercises(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur rechargement exercices :", error);
      toast.error("Impossible de recharger les exercices.");
    }
  }, []);

  const refreshAssignments = useCallback(async () => {
    try {
      const data = await getAllAdherentExerciseResults();
      setAssignments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur rechargement affectations :", error);
      toast.error("Impossible de recharger les affectations.");
    }
  }, []);

  const exerciseProgrammes = useMemo(
    () => getDistinctProgrammesFromExercises(exercises),
    [exercises]
  );

  const assignmentProgrammes = useMemo(
    () => getDistinctProgrammesFromAssignments(assignments),
    [assignments]
  );

  const filteredExercises = useMemo(
    () => filterExercises(exercises, exerciseFilters),
    [exercises, exerciseFilters]
  );

  const filteredAssignments = useMemo(
    () => filterAssignments(assignments, assignmentFilters),
    [assignments, assignmentFilters]
  );

  const stats = useMemo(
    () =>
      calculateAssignmentStats({
        exercises,
        assignments,
        filteredAssignments,
        adherents,
      }),
    [exercises, assignments, filteredAssignments, adherents]
  );

  const openCreateExerciseDrawer = useCallback(() => {
    setCurrentExercise(null);
    setExerciseDrawerMode("create");
    setExerciseDrawerOpen(true);
  }, []);

  const openEditExerciseDrawer = useCallback((exercise) => {
    setCurrentExercise(exercise || null);
    setExerciseDrawerMode("edit");
    setExerciseDrawerOpen(true);
  }, []);

  const openExerciseDetailsDrawer = useCallback((exercise) => {
    setCurrentExercise(exercise || null);
    setExerciseDrawerMode("details");
    setExerciseDrawerOpen(true);
  }, []);

  const closeExerciseDrawer = useCallback(() => {
    setExerciseDrawerOpen(false);
    setCurrentExercise(null);
  }, []);

  const openCreateAssignmentDrawer = useCallback((exercise = null) => {
    setAssignmentPresetExercise(exercise || null);
    setAssignmentCreateDrawerOpen(true);
  }, []);

  const closeCreateAssignmentDrawer = useCallback(() => {
    setAssignmentCreateDrawerOpen(false);
    setAssignmentPresetExercise(null);
  }, []);

  const openEditAssignmentDrawer = useCallback((assignment) => {
    setCurrentAssignment(assignment || null);
    setAssignmentEditDrawerOpen(true);
  }, []);

  const closeEditAssignmentDrawer = useCallback(() => {
    setCurrentAssignment(null);
    setAssignmentEditDrawerOpen(false);
  }, []);

  const handleCreateExercise = useCallback(
    async (payload) => {
      if (!payload?.nom?.trim()) {
        toast.error("Veuillez renseigner le nom de l’exercice.");
        return;
      }

      try {
        setSaving(true);
        await createExercise(payload);
        toast.success("Exercice ajouté.");
        await refreshExercises();
        closeExerciseDrawer();
      } catch (error) {
        console.error("Erreur création exercice :", error);
        toast.error("Erreur lors de la création de l’exercice.");
      } finally {
        setSaving(false);
      }
    },
    [refreshExercises, closeExerciseDrawer]
  );

  const handleUpdateExercise = useCallback(
    async (payload) => {
      if (!currentExercise?.id) return;

      if (!payload?.nom?.trim()) {
        toast.error("Veuillez renseigner le nom de l’exercice.");
        return;
      }

      try {
        setSaving(true);
        await editExercise(currentExercise.id, payload);
        toast.success("Exercice modifié.");
        await refreshExercises();
        closeExerciseDrawer();
      } catch (error) {
        console.error("Erreur modification exercice :", error);
        toast.error("Erreur lors de la modification.");
      } finally {
        setSaving(false);
      }
    },
    [currentExercise?.id, refreshExercises, closeExerciseDrawer]
  );

  const handleDeleteExercise = useCallback(
    async (exercise) => {
      if (!exercise?.id) return;

      if (!window.confirm(`Supprimer l’exercice "${exercise.nom}" ?`)) {
        return;
      }

      try {
        setDeletingId(`exercise-${exercise.id}`);
        await deleteExercise(exercise.id);
        toast.success("Exercice supprimé.");
        await refreshExercises();
        await refreshAssignments();
      } catch (error) {
        console.error("Erreur suppression exercice :", error);
        toast.error("Erreur lors de la suppression.");
      } finally {
        setDeletingId(null);
      }
    },
    [refreshExercises, refreshAssignments]
  );

  const notifyAdherent = useCallback(async (adherentId) => {
    try {
      const users = await getUserIdByAdherentId(adherentId);

      const message =
        "Vous avez un nouvel exercice de préparation physique à effectuer.";

      await Promise.allSettled(
        (users || [])
          .map((item) => item?.UserAdherent?.user_id ?? item?.user_id ?? item?.id)
          .filter(Boolean)
          .map((userId) => sendNotification(message, userId))
      );
    } catch (error) {
      console.warn("Affectation OK, notification impossible :", error);
    }
  }, []);

  const handleCreateAssignments = useCallback(
    async (payload) => {
      const {
        adherentIds = [],
        prepa_physique_id,
        date_realisation,
        repetitions,
        is_done,
        notify,
      } = payload || {};

      if (!prepa_physique_id) {
        toast.error("Veuillez sélectionner un exercice.");
        return;
      }

      if (!adherentIds.length) {
        toast.error("Veuillez sélectionner au moins un adhérent.");
        return;
      }

      try {
        setSaving(true);

        const results = await Promise.allSettled(
          adherentIds.map((adherentId) =>
            addAdherentExerciseResult({
              adherent_id: adherentId,
              prepa_physique_id,
              date_realisation,
              repetitions: repetitions === "" ? null : Number(repetitions),
              is_done: !!is_done,
            })
          )
        );

        const success = results.filter((r) => r.status === "fulfilled").length;
        const failed = results.length - success;

        if (notify && success > 0) {
          await Promise.allSettled(adherentIds.map((id) => notifyAdherent(id)));
        }

        if (success && !failed) {
          toast.success(
            `${success} affectation${success > 1 ? "s" : ""} créée${
              success > 1 ? "s" : ""
            }.`
          );
        } else if (success && failed) {
          toast.warn(`${success} affectation(s), ${failed} erreur(s).`);
        } else {
          toast.error("Aucune affectation n’a pu être créée.");
        }

        await refreshAssignments();

        if (success > 0 && failed === 0) {
          closeCreateAssignmentDrawer();
        }
      } catch (error) {
        console.error("Erreur création affectations :", error);
        toast.error("Erreur lors de l’affectation.");
      } finally {
        setSaving(false);
      }
    },
    [refreshAssignments, closeCreateAssignmentDrawer, notifyAdherent]
  );

  const handleUpdateAssignment = useCallback(
    async (payload) => {
      if (!currentAssignment?.adherent_id || !currentAssignment?.prepa_physique_id) {
        toast.error("Affectation invalide.");
        return;
      }

      try {
        setSaving(true);

        await updateAdherentExerciseResult({
          adherent_id: currentAssignment.adherent_id,
          prepa_physique_id: currentAssignment.prepa_physique_id,
          date_realisation: payload.date_realisation,
          repetitions:
            payload.repetitions === "" ? null : Number(payload.repetitions),
          is_done: !!payload.is_done,
        });

        toast.success("Affectation mise à jour.");
        await refreshAssignments();
        closeEditAssignmentDrawer();
      } catch (error) {
        console.error("Erreur mise à jour affectation :", error);
        toast.error("Erreur lors de la mise à jour.");
      } finally {
        setSaving(false);
      }
    },
    [currentAssignment, refreshAssignments, closeEditAssignmentDrawer]
  );

  const handleDeleteAssignment = useCallback(
    async (assignment) => {
      if (!assignment?.adherent_id || !assignment?.prepa_physique_id) return;

      const name = `${assignment?.Adherent?.prenom || ""} ${
        assignment?.Adherent?.nom || ""
      }`.trim();

      const exerciseName = assignment?.Exercice?.nom || "cet exercice";

      if (
        !window.confirm(
          `Supprimer l’affectation "${exerciseName}" pour ${name || "cet adhérent"} ?`
        )
      ) {
        return;
      }

      try {
        setDeletingId(
          `assignment-${assignment.adherent_id}-${assignment.prepa_physique_id}`
        );

        await deleteAdherentExerciseResult(
          assignment.adherent_id,
          assignment.prepa_physique_id
        );

        toast.success("Affectation supprimée.");
        await refreshAssignments();
      } catch (error) {
        console.error("Erreur suppression affectation :", error);
        toast.error("Erreur lors de la suppression.");
      } finally {
        setDeletingId(null);
      }
    },
    [refreshAssignments]
  );

  return {
    activeTab,
    setActiveTab,

    exercises,
    filteredExercises,
    exerciseFilters,
    setExerciseFilters,
    exerciseProgrammes,

    assignments,
    filteredAssignments,
    assignmentFilters,
    setAssignmentFilters,
    assignmentProgrammes,

    adherents,
    groups,
    seasons,

    stats,
    loading,
    saving,
    deletingId,

    exerciseDrawerOpen,
    exerciseDrawerMode,
    currentExercise,
    openCreateExerciseDrawer,
    openEditExerciseDrawer,
    openExerciseDetailsDrawer,
    closeExerciseDrawer,

    assignmentCreateDrawerOpen,
    assignmentPresetExercise,
    openCreateAssignmentDrawer,
    closeCreateAssignmentDrawer,

    assignmentEditDrawerOpen,
    currentAssignment,
    openEditAssignmentDrawer,
    closeEditAssignmentDrawer,

    handleCreateExercise,
    handleUpdateExercise,
    handleDeleteExercise,

    handleCreateAssignments,
    handleUpdateAssignment,
    handleDeleteAssignment,

    refresh,
  };
}

export default usePrepaPhysiqueDashboard;